import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { 
  Download, Home, ChevronRight, FileText, Calendar
} from "lucide-react";

interface DownloadItem {
  id: number;
  file_name: string;
  file_url: string;
  file_desc: string | null;
  create_time: string;
}

export default function DownloadCenterPage() {
  const [downloads, setDownloads] = useState<DownloadItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    try {
      const resp = await fetch('/api/public/downloads');
      const result = await resp.json();
      if (result.ok && result.data) {
        setDownloads(result.data);
      }
    } catch (err) {
      console.error('获取资料列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 格式化日期时间
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).replace(/\//g, '-');
  };

  // 处理下载
  const handleDownload = (item: DownloadItem) => {
    const link = document.createElement('a');
    link.href = item.file_url;
    link.download = item.file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <Header />
      
      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #003c8c 0%, #0050b8 50%, #0066cc 100%)',
        padding: '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰元素 */}
        <div style={{
          position: 'absolute',
          top: '-80px',
          right: '-50px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          left: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />
        
        <div style={{ width: '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <Download style={{ width: '40px', height: '40px', color: '#fbbf24' }} />
            <h1 style={{ 
              fontSize: '36px', 
              fontWeight: 700, 
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              资料下载
            </h1>
          </div>
          <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.8)', maxWidth: '600px' }}>
            下载招标采购相关资料和操作手册
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        <div style={{ width: '80%', margin: '0 auto', padding: '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <Link
              to="/"
              style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseOver={(e) => e.currentTarget.style.color = '#003c8c'}
              onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}
            >
              <Home style={{ width: '16px', height: '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: '16px', height: '16px', color: '#d1d5db' }} />
            <span style={{ color: '#003c8c', fontWeight: 500 }}>资料下载</span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ flex: 1, width: '80%', margin: '0 auto', padding: '40px 0' }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          {/* 标题栏 */}
          <div style={{
            padding: '24px 32px',
            borderBottom: '1px solid #f3f4f6',
            background: 'linear-gradient(to right, #fafbfc, white)'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>全部资料</h2>
          </div>

          {/* 资料列表 */}
          {loading ? (
            <div style={{ padding: '80px 0', textAlign: 'center' }}>
              <p style={{ color: '#6b7280', fontSize: '16px' }}>加载中...</p>
            </div>
          ) : downloads.length > 0 ? (
            <div>
              {downloads.map((item, index) => (
                <div
                  key={item.id}
                  style={{
                    padding: '24px 32px',
                    borderBottom: index < downloads.length - 1 ? '1px solid #f3f4f6' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '24px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  {/* 左侧信息 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <FileText style={{ width: '20px', height: '20px', color: '#003c8c', flexShrink: 0 }} />
                      <h3 style={{ 
                        fontSize: '16px', 
                        fontWeight: 600, 
                        color: '#1f2937',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {item.file_name}
                      </h3>
                    </div>
                    
                    {item.file_desc && (
                      <p style={{ 
                        fontSize: '14px', 
                        color: '#6b7280', 
                        marginBottom: '8px',
                        marginLeft: '32px'
                      }}>
                        {item.file_desc}
                      </p>
                    )}
                    
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px', 
                      marginLeft: '32px',
                      fontSize: '13px',
                      color: '#9ca3af'
                    }}>
                      <Calendar style={{ width: '14px', height: '14px' }} />
                      <span>{formatDateTime(item.create_time)}</span>
                    </div>
                  </div>

                  {/* 下载按钮 */}
                  <button
                    onClick={() => handleDownload(item)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: '#003c8c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      flexShrink: 0
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0050b8'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#003c8c'}
                  >
                    <Download style={{ width: '16px', height: '16px' }} />
                    下载
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '80px 0', textAlign: 'center' }}>
              <div style={{ 
                width: '100px', 
                height: '100px', 
                margin: '0 auto 24px', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Download style={{ width: '40px', height: '40px', color: '#9ca3af' }} />
              </div>
              <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '8px' }}>暂无资料</p>
              <p style={{ color: '#9ca3af', fontSize: '14px' }}>资料上传后将显示在这里</p>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

